// K2-3138: the customer refresh token lives in the HttpOnly `rut` cookie. vuestorefront-api sets
// and clears it for the browser; SSR reads it from the request and re-sets it after a refresh.
// Client JS never reads or writes it.

export const REFRESH_TOKEN_COOKIE = 'rut'
export const REFRESH_TOKEN_TRANSPORT_HEADER = 'X-Refresh-Token-Transport'
// Refresh session lifetime (K2-3138); cut uses it too, so it never expires before rut.
const SESSION_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000
// Pre-Passport ms-api refresh token; client JS wrote these without HttpOnly.
const LEGACY_REFRESH_TOKEN = /^[0-9a-f]{32}$/i

/** Expiry for both session cookies, cut and rut. */
export function sessionCookieExpires (now: number = Date.now()): Date {
  return new Date(now + SESSION_LIFETIME_MS)
}

/** Same name, domain and path as a Domain= cookie set by old JS; host-only copies need expireHostOnlySessionCookies. */
export function refreshTokenCookieOptions (domain: string | undefined, now: number = Date.now()) {
  return {
    httpOnly: true,
    secure: true,
    sameSite: 'lax',
    path: '/',
    domain,
    expires: sessionCookieExpires(now)
  }
}

interface CookieResponse {
  cookie: (name: string, value: string, options: object) => any,
  clearCookie: (name: string, options: object) => any
}

/**
 * SSR: writes the session's refresh token back to the browser. The value is re-set when it
 * rotated or is a legacy token (which an old client may have left readable to JS).
 */
export function syncRefreshTokenCookie (
  response: CookieResponse,
  { requested, current, domain, now = Date.now() }: { requested?: string | null, current?: string | null, domain?: string, now?: number }
): void {
  if (!current) {
    if (requested) {
      response.clearCookie(REFRESH_TOKEN_COOKIE, { domain, path: '/' })
    }
    return
  }
  if (current !== requested || LEGACY_REFRESH_TOKEN.test(current)) {
    response.cookie(REFRESH_TOKEN_COOKIE, current, refreshTokenCookieOptions(domain, now))
  }
}

const SESSION_COOKIES = ['cut', REFRESH_TOKEN_COOKIE]

function cookieValues (cookieHeader: string | undefined, name: string): string[] {
  if (!cookieHeader) return []
  return cookieHeader.split(';').reduce((values, pair) => {
    const eq = pair.indexOf('=')
    if (eq > 0 && pair.slice(0, eq).trim() === name) {
      const raw = pair.slice(eq + 1).trim()
      try { values.push(decodeURIComponent(raw)) } catch (e) { values.push(raw) }
    }
    return values
  }, [] as string[])
}

/**
 * SSR: a session cookie from the raw Cookie header. A browser can hold a host-only copy next to the
 * Domain= one, in either order (a cookie keeps its creation time): the newest JWT wins, legacy last.
 */
export function sessionCookieValue (cookieHeader: string | undefined, name: string): string | undefined {
  return cookieValues(cookieHeader, name).reduce((best, value) => {
    return best === undefined || sessionCookieRank(value) >= sessionCookieRank(best) ? value : best
  }, undefined as string | undefined)
}

// Legacy lowest, then a JWT by iat; opaque values tie (0) and the later one wins.
function sessionCookieRank (value: string): number {
  if (LEGACY_REFRESH_TOKEN.test(value)) return -1
  const payload = value.split('.')[1]
  if (!payload) return 0
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString()
    const iat = JSON.parse(json).iat
    return typeof iat === 'number' ? iat : 0
  } catch (e) {
    return 0
  }
}

/**
 * SSR: expires host-only cut/rut copies (clearCookie without a domain matches only those). A Domain=
 * cookie never replaces a host-only one, so a legacy pair would otherwise shadow the session.
 * The picked value (sessionCookieValue) may be the host-only copy, so it is re-set on the domain
 * right after (review round 3, R3-2); a later refresh or clear in the same response still wins.
 */
export function expireHostOnlySessionCookies (response: CookieResponse, cookieHeader: string | undefined, domain?: string, now: number = Date.now()): void {
  if (!domain) return
  SESSION_COOKIES.forEach(name => {
    const values = cookieValues(cookieHeader, name)
    if (values.length > 1 || values.some(v => LEGACY_REFRESH_TOKEN.test(v))) {
      response.clearCookie(name, { path: '/' })
      const picked = sessionCookieValue(cookieHeader, name)
      if (picked) {
        response.cookie(name, picked, name === REFRESH_TOKEN_COOKIE
          ? refreshTokenCookieOptions(domain, now)
          : { expires: sessionCookieExpires(now), domain })
      }
    }
  })
}

interface BrowserCookies {
  set: (...args: any[]) => any,
  remove: (...args: any[]) => any
}

const EXPIRED_HOST_ONLY_CUT = 'cut=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'

/**
 * Client: writes cut on the runtime cookie domain (the one SSR uses). vue-cookies is configured from the
 * build-time config, which has no domain, so its default write is host-only; that copy is expired.
 */
export function writeAccessTokenCookie (cookies: BrowserCookies, token: string, domain?: string, doc: { cookie: string } = document, now: number = Date.now()): void {
  if (!domain) {
    cookies.set('cut', token, sessionCookieExpires(now))
    return
  }
  doc.cookie = EXPIRED_HOST_ONLY_CUT
  cookies.set('cut', token, sessionCookieExpires(now), '/', domain)
}

export function removeAccessTokenCookie (cookies: BrowserCookies, domain?: string, doc: { cookie: string } = document): void {
  if (!domain) {
    cookies.remove('cut')
    return
  }
  doc.cookie = EXPIRED_HOST_ONLY_CUT
  cookies.remove('cut', '/', domain)
}

/** Browser calls that issue, use or revoke a refresh token: vuestorefront-api answers with the cookie. */
export function withRefreshTokenCookie (init: any) {
  return Object.assign({}, init, {
    credentials: 'same-origin',
    headers: Object.assign({}, init.headers, { [REFRESH_TOKEN_TRANSPORT_HEADER]: 'cookie' })
  })
}
